import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FundLists } from '../grid/fundWrites.js';
import { useFundWriter } from '../grid/useFundWriter.js';
import { useUndoStack } from '../grid/useUndoStack.js';
import { useResearch } from '../research/useResearch.js';
import { useTRPC } from '../trpc.js';
import { DockedPane, PaneBack } from './DockedPane.js';
import { FundPane } from './FundPane.js';

/** The picklist vocabularies every fund edit is read against. */
export function useFundLists(): FundLists {
  const trpc = useTRPC();
  const sectors = useQuery(trpc.sectors.list.queryOptions());
  const members = useQuery(trpc.members.list.queryOptions());
  return useMemo(
    () => ({
      sectors: (sectors.data ?? []).map((sector) => ({ id: sector.id, label: sector.name })),
      members: (members.data ?? []).map((member) => ({ id: member.id, label: member.name })),
    }),
    [sectors.data, members.data],
  );
}

/**
 * The fund pane on a page that is not the funds grid: a person's row, a
 * round's investor. It brings its own writer and undo, because there is no
 * grid of funds beside it for the two to share.
 */
export function FundPaneHost(props: {
  fundId: string;
  /** The person to open it at, by their role's id. */
  person: string | null;
  onClose: () => void;
  onStep: (delta: 1 | -1) => void;
}): React.JSX.Element {
  const trpc = useTRPC();
  const answer = useQuery(trpc.funds.get.queryOptions({ id: props.fundId }));
  const lists = useFundLists();
  const research = useResearch();
  const writer = useFundWriter(lists);
  const undoStack = useUndoStack();
  const fund = answer.data;

  if (fund === undefined || fund === null) {
    return (
      <DockedPane label="Fund" onClose={props.onClose} onStep={props.onStep}>
        <header className="pane-head">
          <PaneBack onClose={props.onClose} />
          <p className="subtle">
            {answer.isError
              ? answer.error.message
              : answer.isPending
                ? 'Loading…'
                : 'That fund is no longer on file.'}
          </p>
        </header>
      </DockedPane>
    );
  }

  return (
    <FundPane
      fund={fund}
      lists={lists}
      research={research.byFund.get(fund.id)}
      estimate={research.estimate}
      openAt={null}
      focusPerson={props.person}
      writer={writer}
      undoStack={undoStack}
      onStartResearch={() => research.start([fund.id])}
      onClose={props.onClose}
      onStep={props.onStep}
    />
  );
}
